import React from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

const SQUARE_COUNT = 4;

const TetrisLoader = ({ size = 80, color, progress = 0 }) => {
  const theme = useTheme();
  const squareColor = color || theme.palette.primary.main;
  const squareSize = size / 4;
  const bottomSquareSize = squareSize * 1.95;
  const gap = 4;
  const clampedProgress = Math.min(progress, 100);

  return (
    <Box sx={{ width: size, height: size, position: 'relative' }}>
      {Array.from({ length: SQUARE_COUNT }).map((_, i) => {
        // i=0 sits just above the progress square, i=SQUARE_COUNT-1 is the topmost. As the
        // bottom square fills, squares disappear top-down so only the filled square is left
        // by 100% - each one represents a quarter of the fill being "absorbed" into it.
        const disappearAt = (SQUARE_COUNT - i) * (100 / SQUARE_COUNT);
        const visible = clampedProgress < disappearAt;

        return (
          // Outer wrapper handles the disappear transition on its own, untouched-by-the-loop
          // transform/opacity, so it always starts from a clean opacity:1/scale(1) state instead
          // of whatever random mid-bounce frame the inner falling loop happened to be on -
          // that mismatch was what made the disappear read as an inconsistent, sharp jump.
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: '50%',
              bottom: bottomSquareSize + gap + i * (squareSize + gap),
              width: squareSize,
              height: squareSize,
              marginLeft: `-${squareSize / 2}px`,
              opacity: visible ? 1 : 0,
              transform: visible ? 'scale(1) translateY(0)' : `scale(0.3) translateY(${squareSize * 0.6}px)`,
              transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                border: '3px solid',
                borderColor: squareColor,
                borderRadius: 0,
                boxSizing: 'border-box',
                animation: visible ? 'tetris-drop 2s ease-in infinite' : 'none',
                animationDelay: `${i * 0.18}s`,
              }}
            />
          </Box>
        );
      })}

      {/* Progress square: fills bottom-to-top as the page actually finishes loading */}
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: 0,
          width: bottomSquareSize,
          height: bottomSquareSize,
          marginLeft: `-${bottomSquareSize / 2}px`,
          border: '3px solid',
          borderColor: squareColor,
          borderRadius: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%',
            height: `${clampedProgress}%`,
            backgroundColor: squareColor,
            transition: 'height 0.3s ease',
          }}
        />
      </Box>
    </Box>
  );
};

export default TetrisLoader;
